import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
} from "./card";
import { Alert, AlertDescription } from "./alert";

// Loading skeleton components
const LoadingSkeleton = () => (
  <div className="w-full min-w-0">
    {/* Success Banner Skeleton */}
    <div className="flex items-center justify-between gap-4 p-4 rounded-lg border mb-6 bg-gray-50">
      <div className="flex items-center gap-4 animate-pulse">
        <div className="h-8 w-8 rounded-full bg-gray-200" />
        <div className="h-4 w-48 bg-gray-200 rounded" />
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
      {/* Main Content Area */}
      <div className="lg:col-span-8 space-y-6 md:space-y-8">
        {/* Main Score Card */}
        <Card>
          <CardHeader>
            <div className="animate-pulse">
              <div className="h-6 w-48 bg-gray-200 rounded mb-2" />
              <div className="h-4 w-64 bg-gray-200 rounded" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-4">
              {/* Circular Score Skeleton */}
              <div className="md:col-span-3 flex flex-col items-center justify-center">
                <div className="relative w-24 h-24 md:w-32 md:h-32 animate-pulse">
                  <svg className="w-full h-full" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      className="stroke-gray-200"
                      strokeWidth="8"
                    />
                  </svg>
                </div>
              </div>

              {/* Metrics Skeletons */}
              <div className="md:col-span-9 space-y-6">
                {[1, 2, 3, 4, 5].map((index) => (
                  <MetricSkeleton key={index} />
                ))}
              </div>
            </div>

            {/* Alert Skeleton */}
            <div className="mt-6">
              <Alert className="bg-gray-50 border-gray-100">
                <AlertDescription className="flex items-center justify-between animate-pulse">
                  <div className="h-4 w-64 bg-gray-200 rounded" />
                  <div className="h-8 w-32 bg-gray-200 rounded" />
                </AlertDescription>
              </Alert>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[1, 2, 3].map((index) => (
            <StatCardSkeleton key={index} />
          ))}
        </div>
      </div>

      {/* Right Column Skeleton */}
      <div className="lg:col-span-4 space-y-6 md:space-y-8">
        <ActionCardSkeleton />
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-6 w-32 bg-gray-200 rounded" />
              <div className="space-y-3">
                <div className="h-4 w-full bg-gray-200 rounded" />
                <div className="h-4 w-3/4 bg-gray-200 rounded" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
);

// Individual metric skeleton
const MetricSkeleton = () => (
  <div className="space-y-2 animate-pulse">
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <div className="h-5 w-5 bg-gray-200 rounded" />
        <div className="h-4 w-32 bg-gray-200 rounded" />
      </div>
      <div className="h-4 w-16 bg-gray-200 rounded" />
    </div>
    <div className="w-full h-2 bg-gray-200 rounded-full" />
    <div className="h-4 w-3/4 bg-gray-200 rounded" />
  </div>
);

// Stat card skeleton
const StatCardSkeleton = () => (
  <Card>
    <CardHeader>
      <div className="animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="animate-pulse space-y-2">
        <div className="h-8 w-24 bg-gray-200 rounded" />
        <div className="w-full h-2 bg-gray-200 rounded-full" />
        <div className="h-4 w-36 bg-gray-200 rounded" />
      </div>
    </CardContent>
  </Card>
);

// Action card skeleton
const ActionCardSkeleton = () => (
  <Card className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6">
    <div className="animate-pulse space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-5 w-5 bg-white/20 rounded" />
        <div className="h-6 w-48 bg-white/20 rounded" />
      </div>
      <div className="space-y-3">
        {[1, 2, 3].map((index) => (
          <div
            key={index}
            className="w-full h-20 bg-white/10 rounded-xl"
          />
        ))}
      </div>
    </div>
  </Card>
);

// Usage example:
const DashboardContent = ({ isLoading, children }: {
  isLoading: boolean;
  children: React.ReactNode;
}) => {
  if (isLoading) {
    return <LoadingSkeleton />;
  }
  return children;
};

export {
  LoadingSkeleton,
  MetricSkeleton,
  StatCardSkeleton,
  ActionCardSkeleton,
  DashboardContent
};